import React, { useState, useEffect } from 'react';
import { TaskLink } from '../../../types/TaskLink';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  LinkOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import useMessage from 'antd/es/message/useMessage';
import { Button } from 'antd';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';
import '../styles/TaskView.scss';
import { isValidUrl, normalizeUrl } from '../../../utils/urlUtils';
import BaseDropdown from '../../../components/UI/Dropdown/BaseDropdown';

interface TaskLinksProps {
  links: TaskLink[];
  onCreateLink: () => void;
  onEditLink: (link: TaskLink) => void;
  onDeleteLink: (link: TaskLink) => void;
  onDeleteAllLinks: () => void;
}

export const TaskLinks: React.FC<TaskLinksProps> = ({
  links,
  onCreateLink,
  onEditLink,
  onDeleteLink,
  onDeleteAllLinks,
}) => {
  const [favicons, setFavicons] = useState<Record<string, string>>({});
  const [messageApi, messageContextHolder] = useMessage();

  const menuItems = [
    {
      key: 'copy',
      label: <div className="text-brand-grey-dark text-sm font-normal">Copy</div>,
      icon: <CopyOutlined className="text-neutral-900" />,
    },
    {
      key: 'edit',
      label: <div className="text-brand-grey-dark text-sm font-normal">Edit</div>,
      icon: <EditOutlined className="text-neutral-900" />,
    },
    {
      type: 'divider' as const,
      key: 'divider',
    },
    {
      key: 'delete',
      label: <div className="justify-start text-red-600 text-sm font-normal">Delete</div>,
      icon: <DeleteOutlined className="text-red-600" />,
    },
  ];

  const optionsMenuItems = [
    {
      key: 'delete',
      label: <div className="justify-start text-red-600 text-sm font-normal">Delete All</div>,
      icon: <DeleteOutlined className="text-red-600" />,
    },
  ];

  useEffect(() => {
    if (!links) return;

    // Extract domain from URL to get favicon
    const getFaviconUrl = (url: string) => {
      try {
        const domain = new URL(url).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      } catch (e) {
        return null;
      }
    };

    // Create a map of link URLs to their favicon URLs
    const faviconMap: Record<string, string> = {};
    links.forEach((link) => {
      const faviconUrl = getFaviconUrl(link.link);
      if (faviconUrl) {
        faviconMap[link.link] = faviconUrl;
      }
    });

    setFavicons(faviconMap);
  }, [links]);

  const onCopyLink = (link: TaskLink) => {
    navigator.clipboard.writeText(link.link);
    messageApi.success('Link copied!');
  };

  const handleLinkClick = (link: string) => {
    const normalizedUrl = normalizeUrl(link);

    if (isValidUrl(normalizedUrl)) {
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Handle invalid URL (you could show a notification here)
      console.error('Invalid URL:', link);
    }
  };

  if (!links || links.length === 0) {
    return null;
  }

  const sortedLinks = links.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      {messageContextHolder}
      <div className="flex flex-col gap-2 mt-6 task-links-section">
        <div className="h-6 flex items-center gap-1 text-brand-grey-dark text-xs font-semibold ml-[-28px]">
          <div className="relative w-6 h-4 flex items-center justify-center right-0 top-0">
            <BaseDropdown
              menu={{
                items: optionsMenuItems,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  if (e.key === 'delete') {
                    onDeleteAllLinks();
                  }
                },
              }}
              trigger={['click']}
              placement="bottomLeft"
              overlayStyle={{ width: 240 }}
            >
              <Button
                type="link"
                className="h-6 w-6 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-center items-center gap-1 inline-flex task-links-section__options-menu"
              >
                <MoreOutlined className="text-neutral-900" />
              </Button>
            </BaseDropdown>
          </div>
          Links
          <div className="w-5 h-5 flex items-center justify-center bg-neutral-200 text-neutral-900 text-xs font-semibold rounded p-0.5">
            {sortedLinks.length}
          </div>
          <div className="task-links-section__add-link">
            <CustomTooltip title="Add Link">
              <Button
                type="link"
                className="h-6 ml-1 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-start items-center gap-1 inline-flex"
                onClick={onCreateLink}
              >
                <PlusOutlined className="text-neutral-900 hover:text-brand-taskonio-green p-1" />
              </Button>
            </CustomTooltip>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {sortedLinks.map((link: TaskLink) => (
            <CustomTooltip
              key={link.id}
              title={
                link.name !== '' ? (link.name.length > 32 ? link.name : '') : link.link.length > 32 ? link.link : ''
              }
            >
              <div
                className="flex justify-between items-center gap-2 px-3 py-1.5 border border-solid border-neutral-300 rounded-md cursor-pointer relative group"
                onClick={() => handleLinkClick(link.link)}
              >
                {favicons[link.link] && (
                  <img
                    src={favicons[link.link]}
                    alt=""
                    className="text-neutral-900 w-4 h-4"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                {!favicons[link.link] && (
                  <div className="text-neutral-900 w-4 h-4">
                    <GlobalOutlined />
                  </div>
                )}
                <div className="max-w-[240px] text-neutral-900 text-sm font-medium truncate">
                  {link.name !== '' ? link.name : link.link}
                </div>
                <div>
                  <LinkOutlined className="text-neutral-900 group-hover:hidden" />
                  <BaseDropdown
                    menu={{
                      items: menuItems,
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        if (e.key === 'edit') {
                          onEditLink(link);
                        } else if (e.key === 'delete') {
                          onDeleteLink(link);
                        } else if (e.key === 'copy') {
                          onCopyLink(link);
                        }
                      },
                    }}
                    trigger={['click']}
                    placement="bottom"
                    overlayStyle={{ width: 240 }}
                  >
                    <MoreOutlined
                      className="text-neutral-900 hidden group-hover:block cursor-pointer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </BaseDropdown>
                </div>
              </div>
            </CustomTooltip>
          ))}
        </div>
      </div>
    </>
  );
};
